import { ChangeDetectionStrategy, Component, input, output, signal } from '@angular/core';
import { TbsUploadChangeEvent } from '@ngx-terabase/ui/upload';
import { from, map, Observable } from 'rxjs';
import { TbsButtonDirective } from '@ngx-terabase/ui/button';
import { TbsIconComponent } from '@ngx-terabase/ui/icon';
import { TbsUploadComponent } from '@ngx-terabase/ui/upload';

export interface FileUploadInterface {
  base64: string;
  binary: File;
}

@Component({
  selector: 'core-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
  imports: [TbsButtonDirective, TbsIconComponent, TbsUploadComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileUploadComponent {
  public readonly id = input('');
  public readonly note = input<string>();
  public readonly title = input<string>();
  public readonly fileSize = input(10000000);
  public readonly fileTypes = input<string[]>();
  public readonly width = input<number>();
  public readonly height = input<number>();
  public readonly strictDimensions = input(false);

  public readonly emitFile = output<FileUploadInterface>();

  public readonly errorMessage = signal<string | undefined>(undefined);

  public readonly beforeUpload = (file: File): boolean | Observable<boolean> => {
    const fileTypes = this.fileTypes();

    if (fileTypes && !fileTypes.includes(file.type)) {
      this.errorMessage.set('Error: Invalid image format.');

      return false;
    }

    if (file.size > this.fileSize()) {
      this.errorMessage.set('Error: Image size is too big.');

      return false;
    }

    if (file.type.includes('image')) {
      return this.validateDimensions(file);
    }

    return true;
  };

  public onUpload(event: TbsUploadChangeEvent): void {
    this.errorMessage.set('');
    this.readFilesAndEmit(event[0].originalFile);
  }

  private validateDimensions(file: File): Observable<boolean> {
    return from(createImageBitmap(file)).pipe(
      map((bitmap) => {
        const { width, height } = bitmap;

        bitmap.close();

        const requiredWidth = this.width();
        const requiredHeight = this.height();
        const strictDimensions = this.strictDimensions();

        if (
          strictDimensions &&
          requiredWidth &&
          requiredHeight &&
          (requiredWidth !== width || requiredHeight !== height)
        ) {
          this.errorMessage.set('Please check dimensions');

          return false;
        }

        if (
          !strictDimensions &&
          requiredWidth &&
          requiredHeight &&
          (requiredWidth > width || requiredHeight > height)
        ) {
          this.errorMessage.set('Error: Image size is too small.');

          return false;
        }

        return true;
      }),
    );
  }

  private readFilesAndEmit(file: File): void {
    const reader = new FileReader();

    reader.readAsDataURL(file);
    reader.onload = () => {
      this.emitFile.emit({
        base64: reader.result as string,
        binary: file,
      });
    };
  }
}
